import { customFetch } from "@/client/custom-fetch";

import { IZipCode } from "../users/team-form/team-form.types";
import {
  TClient,
  TClientExtended,
  TClientExtendedResponse,
  TCreateClientInput,
  TCreatePartnerMasterInput,
  TRejectionData,
  TUpdateClientInput,
} from "./clients.types";

const API_BASE = "/organizations";

export const getAllClients = async (): Promise<TClientExtended[]> => {
  return customFetch<TClientExtended[]>(`${API_BASE}`);
};

export const getClientById = async (
  clientId: string
): Promise<TClientExtendedResponse> => {
  return customFetch<TClientExtendedResponse>(`${API_BASE}/${clientId}`);
};

export const createClient = async (
  data: TCreateClientInput
): Promise<TClient & { refreshToken: string }> => {
  return customFetch<TClient & { refreshToken: string }>(`${API_BASE}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const createClientWithFullData = async (
  data: TUpdateClientInput
): Promise<TClient & { refreshToken: string }> => {
  const formData = new FormData();

  // Extrai apenas o document
  const { document, ...rest } = data;

  // Payload estrito para createOrganizationDto (POST /partner/master): só campos aceitos pela API
  const typePartner = (rest as any).type_partner;
  const apiData: Record<string, unknown> = {
    name: rest.name ?? "",
    corporate_name: rest.corporate_name ?? "",
    email: rest.email ?? "",
    type_partner:
      typePartner === "business_indicator" ? "business_indicator" : "client",
    business_classification:
      rest.business_classification && ["comercio", "industria", "servicos", "rural"].includes(rest.business_classification)
        ? rest.business_classification
        : (rest.business_classification === "comercial" ? "comercio" : "servicos"),
  };

  const cnpjDigits = rest.cnpj?.replace(/\D/g, "") ?? "";
  if (cnpjDigits.length === 14) {
    apiData.cnpj = cnpjDigits;
  } else {
    const cpfDigits = rest.company_representative?.cpf?.replace(/\D/g, "") ?? "";
    if (cpfDigits.length === 11) {
      apiData.cpf = cpfDigits;
    } else {
      apiData.cnpj = cnpjDigits;
    }
  }

  apiData.address = {
    postal_code: (rest.address?.postal_code ?? "").replace(/\D/g, ""),
    street: rest.address?.street ?? "",
    number: rest.address?.number ?? "",
    complement: rest.address?.complement ?? "",
    neighborhood: rest.address?.neighborhood ?? "",
    city: rest.address?.city ?? "",
    state: rest.address?.state ?? "",
  };

  apiData.company_representative = {
    first_name: rest.company_representative?.first_name ?? "",
    last_name: rest.company_representative?.last_name ?? "",
    personal_email: rest.company_representative?.personal_email ?? "",
    cpf: (rest.company_representative?.cpf ?? "").replace(/\D/g, ""),
    mobile_phone: (rest.company_representative?.mobile_phone ?? "").replace(/\D/g, ""),
  };

  apiData.bank_account = {
    bank_name: rest.bank_account?.bank_name ?? "",
    bank_branch: rest.bank_account?.bank_branch ?? "",
    checking_account: rest.bank_account?.checking_account ?? "",
    checking_account_digit: rest.bank_account?.checking_account_digit ?? "",
    pix_key: rest.bank_account?.pix_key?.trim() ?? "",
  };

  formData.append("data", JSON.stringify(apiData));

  if (document) {
    // company_logo: arquivo único
    if (document.company_logo instanceof File) {
      formData.append("document.company_logo", document.company_logo);
    }

    // cnpj_card: arquivo único
    if (document.cnpj_card instanceof File) {
      formData.append("document.cnpj_card", document.cnpj_card);
    }

    // contract_signed: array de arquivos (até 4 arquivos)
    if (Array.isArray(document.signed_contract)) {
      document.signed_contract.forEach((file: File) => {
        if (file instanceof File) {
          formData.append("document.contract_signed", file);
        }
      });
    }
  }

  return customFetch<TClient & { refreshToken: string }>(`${API_BASE}/partner/master`, {
    method: "POST",
    body: formData,
  });
};

export const updateClient = async (
  clientId: string,
  data: TUpdateClientInput
): Promise<TClient> => {
  return customFetch<TClient>(`${API_BASE}/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
};

export const deleteClient = async (clientId: string): Promise<void> => {
  return customFetch<void>(`${API_BASE}/${clientId}`, {
    method: "DELETE",
  });
};

export const getClientByIdPublic = async (
  clientId: string
): Promise<TClientExtended> => {
  return customFetch<TClientExtended>(`${API_BASE}/${clientId}/public`);
};

export const approveClientData = async (clientId: string): Promise<void> => {
  return customFetch<void>(`${API_BASE}/${clientId}/approve`, {
    method: "PATCH",
  });
};

export const rejectClientData = async (data: TRejectionData): Promise<void> => {
  return customFetch<void>(`${API_BASE}/${data.client_id}/reject`, {
    method: "PATCH",
    body: JSON.stringify({
      rejection_reason: data.rejection_reason,
    }),
  });
};

function toISO8601Date(value: unknown): string {
  if (value == null || value === "") return new Date().toISOString().split("T")[0]!;
  if (value instanceof Date) return value.toISOString().split("T")[0]!;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return new Date().toISOString().split("T")[0]!;
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) return date.toISOString().split("T")[0]!;
  }
  return new Date().toISOString().split("T")[0]!;
}

function ensureSignedTerms(
  value: TUpdateClientInput["signed_terms"]
): { signed: boolean; signed_at: string; ip_address: string } {
  if (value && typeof value === "object" && "signed" in value) {
    return {
      signed: Boolean(value.signed),
      signed_at:
        typeof value.signed_at === "string" && value.signed_at
          ? new Date(value.signed_at).toISOString()
          : new Date().toISOString(),
      ip_address: typeof value.ip_address === "string" ? value.ip_address : "127.0.0.1",
    };
  }
  return {
    signed: true,
    signed_at: new Date().toISOString(),
    ip_address: "127.0.0.1",
  };
}

export const updateClientPublic = async (
  clientId: string,
  data: TUpdateClientInput
): Promise<TClient> => {
  const formData = new FormData();

  // Monta o objeto de dados da organização, excluindo o campo document
  const { document, ...rest } = data;

  // Garante opening_date (ISO 8601), signed_terms (objeto) e tax_classification exigidos pela API
  const organizationData = {
    ...rest,
    opening_date: toISO8601Date(rest.opening_date),
    signed_terms: ensureSignedTerms(rest.signed_terms),
    tax_classification:
      rest.tax_classification && ["simples_nacional", "lucro_presumido", "lucro_real"].includes(rest.tax_classification)
        ? rest.tax_classification
        : "simples_nacional",
    business_classification:
      rest.business_classification && ["comercio", "industria", "servicos", "rural"].includes(rest.business_classification)
        ? rest.business_classification
        : (rest.business_classification === "comercial" ? "comercio" : "servicos"),
  };

  // Adiciona o JSON stringificado dos dados da organização (exceto documentos)
  formData.append("data", JSON.stringify(organizationData));

  // Adiciona arquivos/documentos conforme especificação da API
  if (document) {
    // company_logo: arquivo único
    if (document.company_logo instanceof File) {
      formData.append("document.company_logo", document.company_logo);
    }

    // cnpj_card: arquivo único
    if (document.cnpj_card instanceof File) {
      formData.append("document.cnpj_card", document.cnpj_card);
    }

    // signed_contract: array de arquivos
    if (Array.isArray(document.signed_contract)) {
      document.signed_contract.forEach((file: File) => {
        if (file instanceof File) {
          formData.append("document.signed_contract", file);
        }
      });
    }
  }

  return customFetch<TClient>(`${API_BASE}/${clientId}/public/update`, {
    method: "PUT",
    body: formData,
  });
};

export const fetchZipCode = async (
  zipcode: string
): Promise<IZipCode | null> => {
  const cleanZipcode = zipcode?.replace(/[^0-9]/g, "");
  if (cleanZipcode?.length !== 8) return null;

  try {
    const exists = await customFetch<IZipCode>(`/zipcodes/${cleanZipcode}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return exists || null;
  } catch {
    return null;
  }
};

export const attachUsersToOrganization = async (
  organizationId: string,
  userIds: string[]
): Promise<void> => {
  return customFetch<void>(`${API_BASE}/${organizationId}/users`, {
    method: "POST",
    body: JSON.stringify({ userIds }),
  });
};

export const getPartnerMasterExists = async (
  organizationId: string
): Promise<{ exists: boolean }> => {
  return customFetch<{ exists: boolean }>(
    `/partner-master/${organizationId}/exists`,
    { method: "GET" }
  );
};

export const createPartnerMasterAccess = async (
  organizationId: string,
  data: TCreatePartnerMasterInput
): Promise<{ id: string }> => {
  return customFetch<{ id: string }>(`/partner-master/${organizationId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};
